import React, { useState, useEffect } from 'react';
import { X, Calendar, MapPin, Clock, ChevronRight, AlertCircle, CheckCircle, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../utils/api';

/**
 * TrialBookingModal — single-step booking request.
 *
 * Flow change: the employer NO LONGER pays upfront. They submit a booking
 * request; the candidate is emailed and must accept within 48 hours. Payment
 * only happens after candidate acceptance (handled by a separate flow).
 */
const TrialBookingModal = ({
    isOpen,
    onClose,
    candidate,
    jobId,
    jobTitle,
}) => {
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pricing, setPricing] = useState(null);
    const [bookingData, setBookingData] = useState({
        trialDays: 3,
        startDate: '',
        location: '',
        notes: '',
    });

    useEffect(() => {
        if (isOpen) {
            setSubmitted(false);
            loadPricing();
        }
    }, [isOpen]);

    const loadPricing = async () => {
        try {
            const res = await api.get('/payments/pricing');
            setPricing(res.data);
        } catch (error) {
            // Pricing is informational only — silent failure
        }
    };

    const handleSubmitRequest = async () => {
        if (!bookingData.startDate || !bookingData.location) {
            toast.error('Please fill in all required fields');
            return;
        }

        setLoading(true);
        try {
            await api.post('/trials/book', {
                candidate_id: candidate.id || candidate.candidate_id,
                job_id: jobId,
                trial_days: bookingData.trialDays,
                start_date: bookingData.startDate,
                location: bookingData.location,
                notes: bookingData.notes,
            });
            setSubmitted(true);
            toast.success('Booking request sent to candidate');
        } catch (error) {
            toast.error(error.userMessage || 'Failed to send booking request');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const candidateFirstName = (candidate?.name || candidate?.talent_card?.header?.name || 'the candidate')
        .toString()
        .split(' ')[0];

    const estimatedTotal = pricing
        ? (pricing.total_per_day * bookingData.trialDays).toFixed(2)
        : null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                data-testid="trial-booking-modal"
            >
                {/* Header */}
                <div
                    className="p-6 border-b flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' }}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                {submitted ? 'Request Sent' : 'Request Trial Shift'}
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {candidateFirstName} for {jobTitle}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-white/50 rounded-full transition-colors"
                            data-testid="booking-modal-close"
                        >
                            <X className="h-5 w-5 text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 overflow-y-auto">
                    {submitted ? (
                        <div className="space-y-5 text-center py-2">
                            <div className="mx-auto w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center">
                                <CheckCircle className="h-8 w-8 text-teal-600" />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                    Booking request sent
                                </h3>
                                <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                                    {candidateFirstName} will be notified by email and has{' '}
                                    <strong>48 hours</strong> to accept or decline. You'll receive a payment
                                    link as soon as they accept — only then is the booking confirmed.
                                </p>
                            </div>
                            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200 text-left">
                                <div className="flex items-start space-x-3">
                                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-amber-800">
                                        <p className="font-medium">What happens next?</p>
                                        <ul className="mt-1 space-y-1 text-amber-700">
                                            <li>• You'll see this booking as <strong>Awaiting candidate response</strong> on your dashboard.</li>
                                            <li>• If accepted, the status changes to <strong>Awaiting payment</strong> with a Stripe link.</li>
                                            <li>• If no response in 48 hours, the request expires automatically.</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <Button
                                onClick={onClose}
                                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-6"
                                data-testid="booking-modal-done-btn"
                            >
                                Done
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-5">
                            {/* Trial Duration */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Trial Duration
                                </label>
                                <div className="flex items-center space-x-3">
                                    <Input
                                        type="number"
                                        min={1}
                                        max={10}
                                        value={bookingData.trialDays}
                                        onChange={(e) => {
                                            const val = Math.min(10, Math.max(1, parseInt(e.target.value) || 1));
                                            setBookingData({ ...bookingData, trialDays: val });
                                        }}
                                        className="w-24"
                                        data-testid="trial-days-input"
                                    />
                                    <span className="text-sm text-gray-600">day{bookingData.trialDays !== 1 ? 's' : ''}</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Choose between 1 and 10 days. Most employers start with a 3-5 day trial.
                                </p>
                            </div>

                            {/* Start Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <Calendar className="inline h-4 w-4 mr-1" />
                                    Start Date
                                </label>
                                <Input
                                    type="date"
                                    value={bookingData.startDate}
                                    onChange={(e) => setBookingData({ ...bookingData, startDate: e.target.value })}
                                    min={new Date().toISOString().split('T')[0]}
                                    className="w-full"
                                    data-testid="trial-start-date-input"
                                />
                            </div>

                            {/* Location */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <MapPin className="inline h-4 w-4 mr-1" />
                                    Trial Location
                                </label>
                                <Input
                                    placeholder="e.g., 123 Office Street, London EC1A 1AA"
                                    value={bookingData.location}
                                    onChange={(e) => setBookingData({ ...bookingData, location: e.target.value })}
                                    className="w-full"
                                    data-testid="trial-location-input"
                                />
                            </div>

                            {/* Shift info — informational */}
                            {pricing && (
                                <div className="bg-blue-50 rounded-lg p-4">
                                    <div className="flex items-center space-x-2 text-blue-800">
                                        <Clock className="h-5 w-5" />
                                        <span className="font-medium">Shift Details</span>
                                    </div>
                                    <p className="text-sm text-blue-700 mt-1">
                                        {pricing.shift_details.time} (UK time) ({pricing.shift_details.hours} hours)
                                    </p>
                                    <p className="text-sm text-blue-600 mt-1">
                                        Candidate receives £{pricing.shift_details.hourly_rate}/hour
                                    </p>
                                </div>
                            )}

                            {/* Estimated cost — for visibility, NOT charged now */}
                            {estimatedTotal && (
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-testid="estimated-cost-box">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                                Estimated cost
                                            </p>
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                Not charged now — payment only after candidate accepts.
                                            </p>
                                        </div>
                                        <span className="text-2xl font-bold text-gray-900">
                                            £{estimatedTotal}
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                                <div className="flex items-start space-x-3">
                                    <AlertCircle className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-purple-800">
                                        <p className="font-medium">No payment yet</p>
                                        <p className="text-purple-700 mt-1">
                                            We'll email {candidateFirstName} the invitation. You'll only be asked to pay
                                            once they accept — and you have a 48-hour response window.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <Button
                                onClick={handleSubmitRequest}
                                disabled={loading || !bookingData.startDate || !bookingData.location}
                                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-6"
                                data-testid="send-booking-request-btn"
                            >
                                {loading ? 'Sending request…' : (
                                    <>
                                        <Send className="mr-2 h-5 w-5" />
                                        Send Booking Request
                                        <ChevronRight className="ml-2 h-5 w-5" />
                                    </>
                                )}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TrialBookingModal;
